import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, tap } from 'rxjs';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { SERVICES_CONSTANTS } from '../utils/constants';
import { StringUtils } from '../utils/stringUtils';
import { LoginRequestDTO } from '../model/auth/login-request-dto';
import { LoginResponseDTO } from '../model/auth/login-response-dto';
import { RefreshTokenRequestDTO } from '../model/auth/refresh-token-request-dto';
import { FrontendPermissionDTO } from '../model/auth/frontend-permission-dto';
import { ApiResponse } from '../model/auth/api-response';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private baseUrl: string = environment.baseUrl;
  private readonly ACCESS_TOKEN_KEY = 'accessToken';
  private readonly REFRESH_TOKEN_KEY = 'refreshToken';
  private readonly PERMISSIONS_KEY = 'userPermissions';
  
  // Observable para saber si el usuario está autenticado
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(this.hasValidToken());
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();

  constructor(
    private http: HttpClient,
    private router: Router,
    private stringUtils: StringUtils
  ) {
    // Verificar token al iniciar
    this.checkTokenValidity();
  }

  /**
   * Realizar login con email y password
   */
  login(credentials: LoginRequestDTO): Observable<ApiResponse<LoginResponseDTO>> {
    const url = this.stringUtils.buildUrl(
      this.baseUrl,
      SERVICES_CONSTANTS.ADMIN_PATH.MAIN,
      SERVICES_CONSTANTS.ADMIN_PATH.AUTH.MAIN,
      SERVICES_CONSTANTS.ADMIN_PATH.AUTH.LOGIN
    );

    return this.http.post<ApiResponse<LoginResponseDTO>>(url, credentials).pipe(
      tap(response => {
        if (response.statusCode === '000' && response.data) {
          this.saveTokens(response.data.token, response.data.refreshToken);
          this.isAuthenticatedSubject.next(true);
        }
      })
    );
  }

  /**
   * Refrescar access token usando refresh token
   */
  refreshToken(): Observable<ApiResponse<LoginResponseDTO>> {
    const refreshToken = this.getRefreshToken();
    const currentAccessToken = this.getAccessToken();

    if (!refreshToken || !currentAccessToken) {
      throw new Error('No refresh token available');
    }

    const url = this.stringUtils.buildUrl(
      this.baseUrl,
      SERVICES_CONSTANTS.ADMIN_PATH.MAIN,
      SERVICES_CONSTANTS.ADMIN_PATH.AUTH.MAIN,
      SERVICES_CONSTANTS.ADMIN_PATH.AUTH.REFRESH
    );

    const body: RefreshTokenRequestDTO = {
      refreshToken,
      currentAccessToken
    };

    return this.http.post<ApiResponse<LoginResponseDTO>>(url, body).pipe(
      tap(response => {
        if (response.statusCode === '000' && response.data) {
          this.saveTokens(response.data.token, response.data.refreshToken);
          this.isAuthenticatedSubject.next(true);
        }
      })
    );
  }

  /**
   * Cerrar sesión
   */
  logout(): Observable<ApiResponse<any>> {
    const refreshToken = this.getRefreshToken();

    const url = this.stringUtils.buildUrl(
      this.baseUrl,
      SERVICES_CONSTANTS.ADMIN_PATH.MAIN,
      SERVICES_CONSTANTS.ADMIN_PATH.AUTH.MAIN,
      SERVICES_CONSTANTS.ADMIN_PATH.AUTH.LOGOUT
    );

    return this.http.post<ApiResponse<any>>(url, { refreshToken }).pipe(
      tap(() => {
        this.clearTokens();
        this.isAuthenticatedSubject.next(false);
        this.router.navigate(['/admin/login']);
      })
    );
  }

  /**
   * Obtener permisos de frontend del usuario
   */
  getFrontendPermissions(): Observable<ApiResponse<FrontendPermissionDTO[]>> {
    const url = this.stringUtils.buildUrl(
      this.baseUrl,
      SERVICES_CONSTANTS.ADMIN_PATH.MAIN,
      SERVICES_CONSTANTS.ADMIN_PATH.AUTH.MAIN,
      SERVICES_CONSTANTS.ADMIN_PATH.AUTH.FRONTEND_PERMISSIONS
    );

    return this.http.get<ApiResponse<FrontendPermissionDTO[]>>(url).pipe(
      tap(response => {
        if (response.statusCode === '000' && response.data) {
          this.savePermissions(response.data);
        }
      })
    );
  }

  /**
   * Guardar tokens en localStorage
   */
  private saveTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem(this.ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
  }

  /**
   * Obtener access token
   */
  getAccessToken(): string | null {
    return localStorage.getItem(this.ACCESS_TOKEN_KEY);
  }

  /**
   * Obtener refresh token
   */
  getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  /**
   * Guardar permisos en localStorage
   */
  private savePermissions(permissions: FrontendPermissionDTO[]): void {
    localStorage.setItem(this.PERMISSIONS_KEY, JSON.stringify(permissions));
  }

  /**
   * Obtener permisos guardados
   */
  getStoredPermissions(): FrontendPermissionDTO[] | null {
    const permissions = localStorage.getItem(this.PERMISSIONS_KEY);
    return permissions ? JSON.parse(permissions) : null;
  }

  /**
   * Limpiar todos los tokens y permisos
   */
  clearTokens(): void {
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.PERMISSIONS_KEY);
  }

  /**
   * Verificar si hay un token válido
   */
  hasValidToken(): boolean {
    const token = this.getAccessToken();
    if (!token) {
      return false;
    }

    try {
      // Decodificar JWT para verificar expiración
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expirationTime = payload.exp * 1000; // Convertir a milisegundos
      return Date.now() < expirationTime;
    } catch (error) {
      return false;
    }
  }

  /**
   * Verificar validez del token al iniciar
   */
  private checkTokenValidity(): void {
    const isValid = this.hasValidToken();
    this.isAuthenticatedSubject.next(isValid);
    
    if (!isValid) {
      this.clearTokens();
    }
  }

  /**
   * Verificar si el usuario puede acceder a una ruta
   */
  canAccessRoute(routePath: string): boolean {
    const permissions = this.getStoredPermissions();
    if (!permissions) {
      return false;
    }
    return permissions.some(permission => permission.pagePath === routePath);
  }

  /**
   * Obtener estado de autenticación actual
   */
  isAuthenticated(): boolean {
    return this.isAuthenticatedSubject.value;
  }
}
